import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { find, map } from 'lodash';

import Box from '@mui/material/Box';

import WebAPIClient from '../../../../../api';
import { DEMAND_APP_ID } from '../../../../../store/demand';
import { current, getLocalTimzone } from '../../../../../helpers/date';
import WeatherChartToolbar from './Toolbar';
import WeatherChart from './Chart';

const compileWeatherData = (rawData) =>
  map(rawData, (dataPoint) => ({
    ...dataPoint,
    timestamp: Number(dataPoint.timestamp),
  }));

function WeatherChartContainer() {
  const rtos = useSelector((state) => state.demand.rtos);

  const [loading, setLoading] = useState(false);
  const [rto, setRto] = useState({});
  const [rawData, setRawData] = useState([]);
  const [chartData, setChartData] = useState([]);
  const [selectedMetrics, setSelectedMetrics] = useState([
    'temperature_2m',
    'apparent_temperature',
    'relative_humidity_2m',
    'precipitation_probability',
    'cloud_cover',
  ]);

  const [timezone, setTimezone] = useState(getLocalTimzone());
  const [range, setRange] = useState({
    key: 0,
    label: 'Current',
    ...current(getLocalTimzone().zone),
  });

  const fetchIdRef = useRef(0);

  useEffect(() => {
    setRto(find(rtos, { rto_id: 'PJM' }));
  }, [rtos]);

  useEffect(() => {
    if (rawData.length > 0) {
      setChartData(compileWeatherData(rawData));
    }
  }, [rawData]);

  // Auto-fetch whenever range, metrics, or rto changes
  useEffect(() => {
    if (!rto?.rto_id) return;
    fetchChartData();
    /* eslint-disable-next-line */
  }, [range, selectedMetrics, rto]);

  const fetchChartData = (_range = range) => {
    const fetchId = ++fetchIdRef.current;
    setLoading(true);
    new WebAPIClient(DEMAND_APP_ID)
      .GET(`/resource/weather/${rto.rto_id}`, {
        start: _range.start.unix(),
        end: _range.end.unix(),
      })
      .then((payload) => {
        if (fetchId !== fetchIdRef.current) return;
        setRawData(payload);
      })
      .catch((err) => console.error(err))
      .finally(() => {
        if (fetchId === fetchIdRef.current) setLoading(false);
      });
  };

  return (
    <Box sx={{ width: '100%', mt: 1 }}>
      <WeatherChartToolbar
        selectedMetrics={selectedMetrics}
        setSelectedMetrics={setSelectedMetrics}
        range={range}
        setRange={setRange}
        timezone={timezone}
        setTimezone={setTimezone}
      />
      <Box
        sx={{
          bgcolor: 'background.paper',
          borderRadius: 1,
          boxShadow: 8,
          p: 1,
          mt: 1.5,
          width: '100%',
        }}>
        <WeatherChart
          chartData={chartData}
          selectedMetrics={selectedMetrics}
          range={range}
          timezone={timezone}
          loading={loading}
        />
      </Box>
    </Box>
  );
}

export default WeatherChartContainer;